import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Helmet } from "react-helmet-async";


<Helmet>
  <title>Uniform Catalogue | Explore Our Designs</title>
  <meta name="description" content="Browse our latest uniform catalogue featuring modern designs for hotels, hospitals, and schools with customization options." />
  <meta name="keywords" content="uniform catalogue, uniform designs, hotel uniform catalogue, hospital uniform collection" />

  <meta property="og:title" content="Premium Uniform Manufacturer in India" />
  <meta property="og:description" content="High-quality uniforms for hotels, hospitals, and schools." />
  <meta property="og:type" content="website" />

</Helmet>

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const Catalogue = () => {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={`${isDarkMode ? 'bg-zinc-900 text-white' : 'bg-[#f5f5f5]'} pt-24 min-h-screen`}>

      {/* HEADER */}
      <div className="text-center mb-10">
        <h1 className="text-4xl font-serif">Uniform Catalogue</h1>
        <p className="text-gray-500 mt-2">Premium Professional Collection</p>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mb-16">
        <a
          href="/Malabiscatalouge.pdf"
          download
          className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition"
        >
          <Download size={18} />
          Download Catalogue
        </a>
      </div>

      {/* SECTION 1 */}
      <Section
        title="WAIST COATS"
        imagesLeft={[
          "/productimages/Waist coat2.jpeg",
          "/productimages/Waist coat3.jpeg",
        ]}
        main="/productimages/Waist coat1.jpeg"
        imagesRight={[
          "/productimages/Waist coat4.jpeg",
        ]}
      />

      {/* SECTION 2 */}
      <Section
        title="CLASSIC COATS"
        imagesLeft={[
          "/productimages/Hod1.jpeg",
          "/productimages/Hod2.jpeg"
        ]}
        main="/productimages/Hod3.jpeg"
        imagesRight={[
          "/productimages/Hod4.jpeg"
        ]}
      />

      {/* SECTION 3 (FLOATING) */}
      <FloatingSection />

      {/* SECTION 4 */}
      <Section
        title="FORMAL BLAZERS"
        imagesLeft={[
          "/productimages/img34.jpeg",
          "/productimages/img35.jpeg"
        ]}
        main="/productimages/img32-.jpeg"
        imagesRight={[
          "/productimages/img33.jpeg"
        ]}
      />

      {/* SECTION 5 */}
      <Section
        title="HOSPITAL UNIFORMS"
        imagesLeft={[
          "/productimages/img28.jpeg",
          "/productimages/img29.jpeg"
        ]}
        main="/productimages/img27.jpeg"
        imagesRight={[
          "/productimages/img30.jpeg"
        ]}
      />

    </div>
  );
};

export default Catalogue;



/* ========================= */
/* REUSABLE SECTION */
/* ========================= */

const Section = ({ title, imagesLeft, main, imagesRight }) => {
  return (
    <section className="px-6 mb-24">
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto items-center"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={fadeUp}
      >

        {/* LEFT */}
        <div className="flex flex-col gap-6">
          {imagesLeft.map((img, i) => (
            <img key={i} src={img} className="h-48 object-cover rounded-xl shadow-md hover:scale-105 transition duration-300" />
          ))}
        </div>

        {/* CENTER */}
        <div className="min-h-[350px]">
          <img src={main} className="w-full h-full object-cover rounded-xl shadow-lg hover:scale-105 transition duration-300" />
        </div>

        {/* RIGHT */}
        <div className="flex flex-col gap-6 justify-center">
          <h2 className="text-xl font-semibold tracking-wide">{title}</h2>
          {imagesRight.map((img, i) => (
            <img key={i} src={img} className="h-48 object-cover rounded-xl shadow-md hover:scale-105 transition duration-300" />
          ))}
        </div>

      </motion.div>
    </section>
  );
};



/* ========================= */
/* FLOATING SECTION */
/* ========================= */

const FloatingSection = () => {
  return (
    <section className="relative py-32 min-h-[550px] bg-white overflow-hidden">

      <div className="max-w-6xl mx-auto relative flex items-center justify-center">

        {/* CENTER IMAGE */}
        <motion.img
          src="/productimages/Waist coat3.jpeg"
          className="w-72 rounded-xl shadow-2xl z-10"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        />

        {/* FLOATING IMAGES */}
        <img src="/productimages/Waist coat2.jpeg" className="absolute top-16 left-20 w-36 rounded-xl shadow-lg" />
        <img src="/productimages/Hod1.jpeg" className="absolute top-16 right-20 w-36 rounded-xl shadow-lg" />
        <img src="/productimages/img34.jpeg" className="absolute bottom-16 left-32 w-36 rounded-xl shadow-lg" />
        <img src="/productimages/img33.jpeg" className="absolute bottom-16 right-32 w-36 rounded-xl shadow-lg" />

      </div>

    </section>
  );
};